import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, ArrowRight, MapPin, Info } from "lucide-react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "@/lib/api";
import { jdCss } from "@/lib/jdStyles";
import { useJdDesign } from "@/lib/jdDesignStore";

const ALIGN_CLASS = {
    left: "justify-start",
    center: "justify-center",
    right: "justify-end",
};

// Small reusable Apply pill — text + alignment come straight from the JD doc.
function ApplyPill({ onClick, text, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className="inline-flex items-center gap-2 px-8 h-12 bg-[#0F1F38] text-[#F7F5F0] text-sm font-semibold rounded-full hover:bg-[#1A3254] transition-colors shadow-[0_2px_8px_rgba(15,31,56,0.18)]"
        >
            {text}
            <ArrowRight size={15} strokeWidth={2} />
        </button>
    );
}

export default function JobDescriptionPage() {
    const { id } = useParams();
    const [jd, setJd] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [role, setRole] = useState(null);
    const design = useJdDesign();

    useEffect(() => {
        let alive = true;
        (async () => {
            const { getCachedJd, primeJdCache } = await import(
                "@/lib/publicCache"
            );
            const cached = getCachedJd(id);
            if (cached && alive) {
                setJd(cached);
                document.title = `${cached.job_title} · BluBridge`;
                setLoading(false);
            }
            try {
                const { data } = await api.get(
                    `/public/job-descriptions/${id}`,
                );
                if (!alive) return;
                setJd(data);
                primeJdCache(data);
                document.title = `${data.job_title} · BluBridge`;
                try {
                    const rolesRes = await api.get("/public/openings");
                    const roleObj = rolesRes.data.find(
                        (r) => r.id === data.job_role_id,
                    );
                    if (alive && roleObj) setRole(roleObj);
                } catch (_) {
                    /* eyebrow is nice-to-have; ignore */
                }
            } catch (e) {
                if (!cached) {
                    setNotFound(true);
                    toast.error(apiErrorMessage(e));
                }
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => {
            alive = false;
        };
    }, [id]);

    const openApply = () => {
        if (!jd) return;
        window.open(`/apply/${jd.job_role_id}`, "_blank", "noopener,noreferrer");
    };

    const styles = design?.styles || {};
    const headerLine = useMemo(() => {
        if (!jd?.header_rows?.length) return "";
        return jd.header_rows
            .map((r) => (r.value || "").trim())
            .filter(Boolean)
            .join(" · ");
    }, [jd]);

    // All layout / typography / button controls come from the shared global
    // JD design document. Only the JD's textual content is per-JD.
    const applyText = design?.apply_button_text || "Apply Now";
    const showTop = design?.show_top_apply !== false;
    const showBottom = design?.show_bottom_apply !== false;
    const bottomAlign =
        ALIGN_CLASS[design?.bottom_apply_align] || ALIGN_CLASS.center;

    return (
        <div
            data-testid="jd-page"
            className="min-h-screen bg-[#F7F5F0] text-[#0F1F38]"
            style={{
                fontFamily: "'DM Sans', sans-serif",
                ...jdCss(styles),
            }}
        >
            <header className="border-b border-[#0F1F38]/10 bg-[#F7F5F0]/95 backdrop-blur-xl sticky top-0 z-30">
                <div className="max-w-5xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
                    <a href="/" className="inline-flex items-center">
                        <img
                            src="/blubridge-logo.png"
                            alt="BluBridge"
                            className="h-6 md:h-7 w-auto"
                        />
                    </a>
                    <a
                        href="/"
                        data-testid="jd-back-home"
                        className="text-xs tracking-[0.18em] uppercase font-semibold text-[#0F1F38]/70 hover:text-[#0F1F38] transition-colors"
                    >
                        All Openings
                    </a>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-6 md:px-10 py-12 md:py-16">
                {loading ? (
                    <div className="flex items-center gap-3 text-sm text-[#0F1F38]/70">
                        <Loader2 size={16} className="animate-spin" />
                        Loading job description…
                    </div>
                ) : notFound || !jd ? (
                    <div
                        data-testid="jd-not-found"
                        className="border border-[#0F1F38]/20 p-10 md:p-14"
                    >
                        <div className="text-[11px] tracking-[0.18em] uppercase text-[#0F1F38]/60 mb-3">
                            Not found
                        </div>
                        <h1 className="font-bold text-3xl md:text-4xl leading-[1.05] text-[#0F1F38]">
                            This role isn&apos;t available.
                        </h1>
                        <p className="mt-4 text-[#0F1F38]/75">
                            It may have been removed. Head back to{" "}
                            <a href="/" className="link-underline">
                                all openings
                            </a>
                            .
                        </p>
                    </div>
                ) : (
                    <article data-testid="jd-card" className="pb-8">
                        {/* Hero — eyebrow, title, location + inline top Apply
                            button on the right side of the same row. */}
                        <div className="pb-8 border-b border-[#0F1F38]/12 flex items-start justify-between gap-6">
                            <div className="min-w-0 flex-1">
                                <div
                                    data-testid="jd-eyebrow"
                                    className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#0F1F38]/80 mb-4"
                                    style={jdCss(styles, "subtitle")}
                                >
                                    {(role?.name ||
                                        jd.job_title ||
                                        "Open Role").toUpperCase()}
                                </div>
                                <h1
                                    data-testid="jd-title"
                                    className="font-bold tracking-tight text-[#0F1F38] leading-[1.05] text-4xl md:text-5xl lg:text-[56px]"
                                    style={jdCss(styles, "title")}
                                >
                                    {jd.job_title}
                                </h1>
                                {headerLine && (
                                    <div
                                        data-testid="jd-header-line"
                                        className="mt-5 flex items-center gap-2 text-[15px] text-[#0F1F38]/75"
                                        style={jdCss(styles, "header_line")}
                                    >
                                        <MapPin
                                            size={15}
                                            strokeWidth={1.7}
                                            className="text-[#0F1F38]/60 flex-shrink-0"
                                        />
                                        <span>{headerLine}</span>
                                    </div>
                                )}
                            </div>
                            {showTop && (
                                <div
                                    data-testid="jd-top-apply-wrap"
                                    className="flex-shrink-0 pt-1"
                                >
                                    <ApplyPill
                                        onClick={openApply}
                                        text={applyText}
                                        testid="jd-apply-top"
                                    />
                                </div>
                            )}
                        </div>

                        {/* Encouragement callout — only if admin set text */}
                        {jd.callout_text && (
                            <div
                                data-testid="jd-callout"
                                className="mt-8 flex gap-3 border-l-2 border-[#0F1F38] bg-[#0F1F38]/[0.04] px-4 py-3.5 text-[14px] text-[#0F1F38]/85"
                                style={jdCss(styles, "callout")}
                            >
                                <Info
                                    size={16}
                                    strokeWidth={1.7}
                                    className="mt-0.5 text-[#0F1F38]/70 flex-shrink-0"
                                />
                                <p className="whitespace-pre-wrap">
                                    {jd.callout_text}
                                </p>
                            </div>
                        )}

                        {/* Body header — inline "Label: value" pairs */}
                        {(jd.about_rows || []).length > 0 && (
                            <div
                                data-testid="jd-body-header"
                                className="mt-10 grid gap-x-10 gap-y-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
                            >
                                {(jd.about_rows || []).map((r, i) => (
                                    <div
                                        key={i}
                                        data-testid={`jd-body-header-row-${i}`}
                                        className="text-[15px] md:text-[16px] leading-relaxed"
                                    >
                                        <span
                                            className="font-bold text-[#0F1F38]"
                                            style={jdCss(
                                                styles,
                                                "body_header_label",
                                                `body_header_label_${i}`,
                                            )}
                                        >
                                            {r.label}:
                                        </span>{" "}
                                        <span
                                            className="text-[#0F1F38]/85"
                                            style={jdCss(
                                                styles,
                                                "body_header_value",
                                                `body_header_value_${i}`,
                                            )}
                                        >
                                            {r.value}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Body — descriptive sections */}
                        <div className="mt-12 space-y-10">
                            {(jd.sections || []).map((s, i) => {
                                const heading = (
                                    s.heading || "Untitled section"
                                ).replace(/:\s*$/, "");
                                return (
                                    <section
                                        key={i}
                                        data-testid={`jd-section-${i}`}
                                    >
                                        <h3
                                            className="text-[#0F1F38] font-bold text-2xl md:text-[26px] leading-tight mb-4"
                                            style={jdCss(
                                                styles,
                                                "section_heading",
                                                `section_heading_${i}`,
                                            )}
                                        >
                                            {heading}
                                        </h3>
                                        <div
                                            className="whitespace-pre-wrap text-[#0F1F38]/90 text-[16px] md:text-[17px] leading-[1.75]"
                                            style={jdCss(
                                                styles,
                                                "section_content",
                                                `section_content_${i}`,
                                            )}
                                        >
                                            {s.content}
                                        </div>
                                    </section>
                                );
                            })}
                            {(!jd.sections || jd.sections.length === 0) && (
                                <p className="text-sm text-[#4B5563]">
                                    No description available yet.
                                </p>
                            )}
                        </div>

                        {/* Bottom apply button — no default filler text */}
                        {showBottom && (
                            <div
                                data-testid="jd-bottom-apply-wrap"
                                className={`mt-14 pt-10 border-t border-[#0F1F38]/12 flex ${bottomAlign}`}
                            >
                                <ApplyPill
                                    onClick={openApply}
                                    text={applyText}
                                    testid="jd-apply-now"
                                />
                            </div>
                        )}
                    </article>
                )}
            </main>

            <footer className="mt-6 border-t border-[#0F1F38]/10 bg-[#0F1F38]">
                <div className="max-w-5xl mx-auto px-6 md:px-10 py-4 text-xs text-[#F7F5F0]/70">
                    Copyright {new Date().getFullYear()} ©{" "}
                    <a
                        href="https://blubridge.ai"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-semibold text-[#F7F5F0] hover:underline"
                    >
                        Blubridge.com
                    </a>
                </div>
            </footer>
        </div>
    );
}
